// CSV export utility for web-only usage
// Converts a 2D array of values into a CSV and triggers a browser download

// Accepts strings, numbers, booleans, nulls and Dates; formats and escapes as needed
export type CsvCell = string | number | boolean | null | Date | undefined;

const isWebEnv = typeof globalThis !== 'undefined' && !!(globalThis as any).document && !!(globalThis as any).window;

function toCsvCellValue(value: CsvCell): string {
	if (value === null || value === undefined) return '';
	if (value instanceof Date && !isNaN(value.getTime())) {
		// Use ISO date (YYYY-MM-DD) for CSV consistency
		return value.toISOString().slice(0, 10);
	}
	// Booleans as Yes/No for readability
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	// Numbers and strings
	return String(value);
}

function escapeCsvField(raw: string): string {
	// If field contains comma, quotes, or newline, wrap in quotes and double any existing quotes
	const needsQuoting = /[",\n\r]/.test(raw);
	if (!needsQuoting) return raw;
	return '"' + raw.replace(/"/g, '""') + '"';
}

export function buildCsvContent(rows: CsvCell[][]): string {
	const lines = rows.map((row) => row.map((cell) => escapeCsvField(toCsvCellValue(cell))).join(','));
	// Use CRLF for better Excel compatibility
	return lines.join('\r\n');
}

export function downloadCsvFrom2DArray(rows: CsvCell[][], filename: string): void {
	if (!rows || rows.length === 0) return;
	if (!isWebEnv) {
		console.warn('downloadCsvFrom2DArray called outside web environment. No-op.');
		return;
	}
	const csvContent = buildCsvContent(rows);
	// Prepend UTF-8 BOM for Excel compatibility and encode for data URI
	const dataUri = 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURIComponent(csvContent);
	const globalAny = globalThis as any;
	const doc = globalAny.document as any;
	const link = doc.createElement('a');
	link.setAttribute('href', dataUri);
	link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
	link.style.visibility = 'hidden';
	doc.body.appendChild(link);
	link.click();
	doc.body.removeChild(link);
}
